import { proStudioUrqlGql as urqlGql } from '@pypestream/api-services';
import { reportDegradedLoad } from './helpers/report-degraded-load';

import {
  parseActionNodeNames,
  combineActionNodeNames,
  resolveActionNodeNames,
  readCachedActionNodeNames,
  writeCachedActionNodeNames,
} from '../../stores/utils';
import { ProStudioContext, ProStudioEvents } from '../prostudio.xstate.types';

// This invoke lives in the `botDataLoading` parallel region whose `onDone`
// only fires once EVERY region reaches `final`, so a slow/hung response would
// otherwise stall bot loading (the urql query has no timeout of its own). Cap
// the wait so a backend hiccup degrades to the fallback instead of blocking.
const FETCH_TIMEOUT_MS = 5_000;

const withTimeout = <T>(p: PromiseLike<T>, ms: number): Promise<T> =>
  Promise.race([
    Promise.resolve(p),
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error('getActionNodeNames timeout')), ms)
    ),
  ]);

/**
 * Loads the dynamic list of native action-node command names (system + library)
 * from the backend `getActionNodeNames` query (PSTUDIO-2998), replacing the old
 * hardcoded `standardFunctions` list.
 *
 * Deliberately NON-fatal AND time-bounded: a failed/unauthorized/malformed/slow
 * response must not block bot loading nor blank the validator's command list. On
 * any failure (error, timeout, empty/malformed payload) it resolves to the
 * 3-tier fallback — the localStorage last-known-good (updated on every successful
 * fetch) or, failing that, the static bootstrap default. On success it refreshes
 * the last-known-good cache with the combined list.
 */
export const loadActionNodeNames = async (
  _ctx: ProStudioContext,
  _event: ProStudioEvents
): Promise<{ actionNodeNames: string[] }> => {
  const cached = readCachedActionNodeNames();

  try {
    const { data, error } = await withTimeout(
      urqlGql.getActionNodeNames(),
      FETCH_TIMEOUT_MS
    );
    if (error) throw error;

    const parsed = parseActionNodeNames(data?.admin_?.getActionNodeNames);

    if (parsed) {
      const combined = combineActionNodeNames(parsed);
      if (combined.length) {
        // Refresh the last-known-good only for a valid, non-empty live list.
        writeCachedActionNodeNames(combined);
        return { actionNodeNames: combined };
      }
    }

    // Reachable but empty/malformed → fall back rather than blank the list.
    return {
      actionNodeNames: resolveActionNodeNames({ fetched: null, cached }),
    };
  } catch (error) {
    // The bare `catch {}` this replaces threw the error object away, so the
    // fallback to the cached list was untraceable even in the console. The
    // fallback itself stays: an empty Actions list is worse than a stale one.
    reportDegradedLoad(error, 'load-action-node-names', {
      cachedCount: cached?.length ?? 0,
    });

    return {
      actionNodeNames: resolveActionNodeNames({ fetched: null, cached }),
    };
  }
};
